import React, { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import * as submasterService from '../../services/submaster.service';
import * as clientService from '../../services/client.service';
import * as companyService from '../../services/company.service';
import { FaUser, FaCheckCircle } from 'react-icons/fa';
import BuyerLedgerClientConfiguration from './BuyerLedgerClientConfiguration';
import BuyerLedgerLedgerConfiguration from './BuyerLedgerLedgerConfiguration';
import BuyerLedgerShippingDetails from './BuyerLedgerShippingDetails';
import BuyerLedgerContactDetails from './BuyerLedgerContactDetails';
import BuyerLedgerDomesticExportSection from './BuyerLedgerDomesticExportSection';
import {
  INITIAL_FORM,
  SUBMASTER_TYPES,
  emptyShippingRow,
  mapClientToForm,
  validateBuyerLedgerForm,
  buildClientSavePayload,
  MAX_SHIPPING_TABS,
} from './utils';
import { BUYER_LEDGER, ROUTES } from '../../constant';
import './BuyerLedger.css';

const BuyerLedgerCreation = () => {
  const params = useParams();
  const blcRef = useRef();
  const editClientId = params.id || null;

  const [options, setOptions] = useState({});
  const [companyBranches, setCompanyBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ ...INITIAL_FORM, _id: '' });
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [errors, setErrors] = useState({});
  const [activeShippingTab, setActiveShippingTab] = useState(0);

  useEffect(() => {
    if (editClientId) {
      const loadClient = async () => {
        try {
          const client = await clientService.getClientById(editClientId);
          if (client) {
            setForm(mapClientToForm(client));
            setActiveShippingTab(0);
          }
        } catch (err) {
          setMessage({ type: 'danger', text: BUYER_LEDGER.ALERT_LOAD_FAILED });
          console.error(err);
        }
      };
      loadClient();
    } else {
      setForm({ ...INITIAL_FORM, _id: '' });
      setActiveShippingTab(0);
    }
  }, [editClientId]);

  useEffect(() => {
    let mounted = true;
    const loadAll = async () => {
      setLoading(true);
      try {
        const results = {};
        await Promise.all(
          SUBMASTER_TYPES.map(async (t) => {
            const items = await submasterService.getItems(t.key);
            results[t.key] = Array.isArray(items) ? items : items.data || [];
          }),
        );
        const companyData = await companyService.getCompanyDetails();
        const branches = companyData?.branches || [];
        if (!mounted) return;
        setOptions(results);
        setCompanyBranches(branches);
      } catch (e) {
        console.error('Failed to load data', e);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    loadAll();
    return () => {
      mounted = false;
    };
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const updateShippingField = (tabIndex, field, value) => {
    setForm((f) => {
      const next = [...(f.shippingAddresses || [])];
      if (!next[tabIndex]) return f;
      next[tabIndex] = { ...next[tabIndex], [field]: value };
      return { ...f, shippingAddresses: next };
    });
  };

  const addShippingTab = () => {
    setForm((f) => {
      const list = f.shippingAddresses || [];
      if (list.length >= MAX_SHIPPING_TABS) return f;
      const next = [...list, emptyShippingRow()];
      const newIdx = next.length - 1;
      queueMicrotask(() => setActiveShippingTab(newIdx));
      return { ...f, shippingAddresses: next };
    });
  };

  const removeShippingTab = (tabIndex) => {
    setForm((f) => {
      const rows = [...(f.shippingAddresses || [])];
      if (rows.length <= 1) return f;
      rows.splice(tabIndex, 1);
      return { ...f, shippingAddresses: rows };
    });
    setActiveShippingTab((cur) => {
      if (tabIndex < cur) return cur - 1;
      if (tabIndex === cur) return Math.max(0, cur - 1);
      return cur;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage(null);
    const v = validateBuyerLedgerForm(form);
    if (Object.keys(v).length) {
      setErrors(v);
      setMessage({ type: 'danger', text: BUYER_LEDGER.ALERT_SAVE_VALIDATION });
      return;
    }
    setSaving(true);
    try {
      const payload = buildClientSavePayload(form);
      const saved = await clientService.saveClient({ id: form._id || '', ...payload });
      const isEdit = !!form._id;
      const savedCode = saved.client_id || saved.clientId;

      blcRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setMessage({
        type: 'success',
        text: isEdit ? BUYER_LEDGER.SUCCESS_UPDATED(savedCode) : BUYER_LEDGER.SUCCESS_CREATED(savedCode),
      });
      if (!isEdit) {
        setTimeout(() => {
          window.location.href = ROUTES.CRM_ADD_CLIENT;
        }, 1500);
      }
    } catch (err) {
      setMessage({ type: 'danger', text: BUYER_LEDGER.ALERT_SAVE_FAILED });
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="blc-page-bg" ref={blcRef}>
      <div className="container-lg mt-2">
        <div className="mb-4">
          <div className="d-flex align-items-center gap-3 mb-2">
            <div className="blc-page-icon">
              <FaUser size={28} />
            </div>
            <div>
              <h1 className="mb-0 blc-page-title">
                {editClientId ? BUYER_LEDGER.PAGE_TITLE_EDIT : BUYER_LEDGER.PAGE_TITLE_CREATE}
              </h1>
              <p className="mb-0 text-muted">
                {editClientId ? BUYER_LEDGER.SUBTITLE_EDIT : BUYER_LEDGER.SUBTITLE_CREATE}
              </p>
            </div>
          </div>
        </div>

        {message && (
          <div
            className={`alert alert-${message.type} alert-dismissible fade show rounded-3 mb-4`}
            role="alert"
          >
            <strong>{message.type === 'success' ? BUYER_LEDGER.ALERT_SUCCESS_PREFIX : BUYER_LEDGER.ALERT_ERROR_PREFIX}</strong>{' '}
            {message.text}
            <button type="button" className="btn-close" onClick={() => setMessage(null)} aria-label="Dismiss" />
          </div>
        )}

        {loading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">{BUYER_LEDGER.LOADING}</span>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <BuyerLedgerClientConfiguration form={form} errors={errors} onChange={handleChange} />

            <BuyerLedgerLedgerConfiguration
              form={form}
              errors={errors}
              options={options}
              companyBranches={companyBranches}
              onChange={handleChange}
            />

            <BuyerLedgerShippingDetails
              shippingAddresses={form.shippingAddresses}
              activeShippingTab={activeShippingTab}
              onTabChange={setActiveShippingTab}
              onAddTab={addShippingTab}
              onRemoveTab={removeShippingTab}
              onShippingFieldChange={updateShippingField}
            />

            <BuyerLedgerContactDetails form={form} errors={errors} onChange={handleChange} />

            <BuyerLedgerDomesticExportSection form={form} errors={errors} onChange={handleChange} />

            <div className="mb-4 d-flex justify-content-between">
              <button type="submit" disabled={saving} className="btn btn-lg blc-submit-btn">
                <FaCheckCircle className="me-2" aria-hidden />
                {saving ? BUYER_LEDGER.BTN_SAVING : BUYER_LEDGER.BTN_SAVE}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default BuyerLedgerCreation;
